import { AlertCircle, Wallet } from "lucide-react";
import { type ReactNode, useMemo } from "react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaProgress, type ColocaProgressSegment } from "@/shared/components/feedback/ColocaProgress";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { RuntimeWalletCurrencySummary, RuntimeWalletsSummary } from "../../types/payments-wallets.types";
import {
  hasHeld,
  orderCurrencySummaries,
  overviewFractionDigits,
  shareOf,
} from "../../utils/walletsOverview";
import { CurrencyMark } from "./CurrencyMark";

export interface WalletsCurrencySummaryProps {
  summary: RuntimeWalletsSummary | null;
  isLoading: boolean;
  error: string | null;
  /** The currency the list is filtered to; its card reads as pressed. */
  selectedCurrency: string | null;
  onSelectCurrency: (currency: string | null) => void;
  masked: boolean;
  /** With "Solo con saldo" on: a currency with nothing to back (available and blocked both zero) has no card. */
  hideEmpty?: boolean;
}

/**
 * One card per currency: what Coloca must back (available + blocked), how that splits, and who holds the
 * most. A card is also the currency filter of the list under it — pressing it again clears it.
 */
export function WalletsCurrencySummary({
  summary,
  isLoading,
  error,
  selectedCurrency,
  onSelectCurrency,
  masked,
  hideEmpty = false,
}: WalletsCurrencySummaryProps) {
  const { t } = useTranslations("wallets");
  const currencies = useMemo(() => {
    const rows = summary ? orderCurrencySummaries(summary.currencies) : [];
    // The selected currency keeps its card, so the filter it drives can always be cleared from there.
    return hideEmpty ? rows.filter((row) => hasHeld(row) || row.currency === selectedCurrency) : rows;
  }, [hideEmpty, selectedCurrency, summary]);
  if (error) {
    return <ColocaAlert variant="destructive" icon={AlertCircle} text={t("summary_error")} />;
  }

  if (isLoading && !summary) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <ColocaSkeleton key={index} className="h-[188px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (currencies.length === 0) {
    return <ColocaEmpty surface="card" icon={Wallet} title={t("summary_empty")} />;
  }

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-3 transition-opacity sm:grid-cols-2 xl:grid-cols-4",
        isLoading && "opacity-60",
      )}
      aria-busy={isLoading}
    >
      {currencies.map((row) => (
        <CurrencyCard
          key={row.currency}
          row={row}
          masked={masked}
          selected={selectedCurrency === row.currency}
          onSelect={() => onSelectCurrency(selectedCurrency === row.currency ? null : row.currency)}
        />
      ))}
    </div>
  );
}

interface CurrencyCardProps {
  row: RuntimeWalletCurrencySummary;
  masked: boolean;
  selected: boolean;
  onSelect: () => void;
}

function CurrencyCard({ row, masked, selected, onSelect }: CurrencyCardProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const digits = overviewFractionDigits(row.currency);
  const count = (value: number) => new Intl.NumberFormat(activeLanguage).format(value);
  const percent = (value: number) =>
    new Intl.NumberFormat(activeLanguage, {
      style: "percent",
      maximumFractionDigits: value < 0.1 ? 1 : 0,
    }).format(value);

  // The bar is the whole of what sits in these wallets; held is its first two parts.
  const whole = [row.available, row.blocked, row.pending].reduce((sum, value) => sum + Number(value), 0);
  const part = (value: string) => (whole > 0 ? (Number(value) / whole) * 100 : 0);
  const segments: ColocaProgressSegment[] = [
    { key: "available", value: part(row.available), tone: "success", label: t("summary_available") },
    { key: "blocked", value: part(row.blocked), tone: "warning", label: t("summary_blocked") },
    { key: "pending", value: part(row.pending), tone: "neutral", label: t("summary_pending") },
  ];
  const topShare = row.top_holder ? shareOf(row.top_holder.held, row.held) : null;
  const hasPending = Number(row.pending) > 0;

  const amount = (value: string) => (
    <ColocaAmountDisplay
      value={value}
      fractionDigits={digits}
      masked={masked}
      size="xs"
      className="text-foreground"
    />
  );

  return (
    <ColocaKpiCard
      selected={selected}
      onSelect={onSelect}
      title={t("summary_select_aria", { currency: row.currency })}
      label={
        <span className="flex items-center gap-2">
          <CurrencyMark currency={row.currency} />
          <span aria-hidden="true">·</span>
          <span>{t("summary_held")}</span>
        </span>
      }
      value={
        <ColocaAmountDisplay
          value={row.held}
          currency={row.currency}
          fractionDigits={digits}
          masked={masked}
          className="text-2xl font-semibold tracking-tight"
        />
      }
      meta={
        <span className="flex flex-col gap-2.5">
          {whole > 0 && (
            <ColocaProgress
              value={part(row.available) + part(row.blocked)}
              size="sm"
              segments={segments}
              aria-label={t("summary_held")}
            />
          )}
          <span className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
            <LegendLabel tone="bg-success">{t("summary_available")}</LegendLabel>
            <span className="text-right">{amount(row.available)}</span>
            <LegendLabel tone="bg-warning">{t("summary_blocked")}</LegendLabel>
            <span className="text-right">{amount(row.blocked)}</span>
            {hasPending && (
              <>
                <LegendLabel tone="bg-muted-foreground/40">{t("summary_pending")}</LegendLabel>
                <span className="text-right">{amount(row.pending)}</span>
              </>
            )}
          </span>
          <span className="flex flex-col gap-0.5 border-t border-border pt-2">
            <span>
              {t("summary_wallets", { funded: count(row.funded_wallets), wallets: count(row.wallets) })}
            </span>
            {row.top_holder && topShare !== null && (
              <span className="truncate" title={row.top_holder.organization_name}>
                {t("summary_top_holder", {
                  name: row.top_holder.organization_name,
                  share: percent(topShare),
                })}
              </span>
            )}
          </span>
        </span>
      }
    />
  );
}

function LegendLabel({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("size-1.5 shrink-0 rounded-full", tone)} aria-hidden="true" />
      {children}
    </span>
  );
}
